import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Calendar as CalendarIcon, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EquipmentDetail() {
  const { id } = useParams();
  const { language } = useLanguage();
  const [equipment, setEquipment] = useState(null);
  const [suggestedItems, setSuggestedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rentals, setRentals] = useState([]);
  const [requestType, setRequestType] = useState('specific');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    start_date: '',
    end_date: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEquipment();
    fetchRentals();
    fetchSuggestedItems();
    // Scroll to top when navigating to this page
    window.scrollTo(0, 0);
  }, [id]);

  const fetchEquipment = async () => {
    try {
      const res = await axios.get(`${API}/equipment/${id}`);
      setEquipment(res.data);
    } catch (error) {
      console.error('Error fetching equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRentals = async () => {
    try {
      const res = await axios.get(`${API}/rentals/equipment/${id}`);
      setRentals(res.data);
    } catch (error) {
      console.error('Error fetching rentals:', error);
    }
  };

  const fetchSuggestedItems = async () => {
    try {
      const res = await axios.get(`${API}/equipment/${id}/suggested`);
      setSuggestedItems(res.data);
    } catch (error) {
      console.error('Error fetching suggested items:', error);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  // Get all images
  const images = equipment?.images?.length > 0 
    ? equipment.images 
    : (equipment?.image_url ? [equipment.image_url] : []);

  // Get localized content (name stays in original language)
  const localizedName = equipment?.name;
  const localizedSubheading = equipment?.subheading;
  const localizedCategory = language === 'fr' && equipment?.category_fr ? equipment.category_fr : equipment?.category;
  const localizedDescription = language === 'fr' && equipment?.description_fr ? equipment.description_fr : equipment?.description;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Get blocked dates
  const getBlockedDates = () => {
    const blocked = [];
    rentals.forEach(rental => {
      if (rental.start_date && rental.end_date) {
        const start = new Date(rental.start_date);
        const end = new Date(rental.end_date);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          blocked.push(new Date(d));
        }
      }
    });
    return blocked;
  };

  const blockedDates = getBlockedDates();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/rentals/request`, {
        equipment_id: equipment.id,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        start_date: requestType === 'specific' ? formData.start_date : null,
        end_date: requestType === 'specific' ? formData.end_date : null,
        message: formData.message,
        request_type: requestType
      });

      toast.success(language === 'fr' 
        ? 'Demande envoyée! Nous vous contacterons bientôt.' 
        : 'Request sent! We\'ll contact you soon.');
      setDialogOpen(false);
      setFormData({ customer_name: '', customer_email: '', customer_phone: '', start_date: '', end_date: '', message: '' });
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const labels = {
    en: {
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      available: 'Available',
      rented: 'Rented',
      viewCalendar: 'View Availability',
      requestRental: 'Request Rental',
      specificDates: 'Request Specific Dates',
      generalInquiry: 'General Inquiry',
      yourName: 'Your Name',
      email: 'Email',
      phone: 'Phone',
      startDate: 'Start Date',
      endDate: 'End Date',
      message: 'Message / Questions',
      submit: 'Submit Request',
      submitting: 'Sending...',
      availabilityFor: 'Availability for',
      blockedDates: 'Red dates are unavailable',
      close: 'Close',
      photos: 'photos',
      back: 'Back',
      specifications: 'Specifications',
      category: 'Category',
      rates: 'Rental Rates',
      suggestedAccessories: 'You might also need'
    },
    fr: {
      daily: 'Journalier',
      weekly: 'Hebdomadaire',
      monthly: 'Mensuel',
      available: 'Disponible',
      rented: 'Loué',
      viewCalendar: 'Voir disponibilité',
      requestRental: 'Demande de location',
      specificDates: 'Dates spécifiques',
      generalInquiry: 'Demande générale',
      yourName: 'Votre nom',
      email: 'Courriel',
      phone: 'Téléphone',
      startDate: 'Date de début',
      endDate: 'Date de fin',
      message: 'Message / Questions',
      submit: 'Envoyer la demande',
      submitting: 'Envoi...',
      availabilityFor: 'Disponibilité pour',
      blockedDates: 'Les dates en rouge sont indisponibles',
      close: 'Fermer',
      photos: 'photos',
      back: 'Retour',
      specifications: 'Spécifications',
      category: 'Catégorie',
      rates: 'Tarifs de location',
      suggestedAccessories: 'Vous pourriez aussi avoir besoin'
    }
  };

  const l = labels[language];

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!equipment) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-muted-foreground">Equipment not found</p>
      </div>
    );
  }

  return (
    <div className="grain-texture pt-20" data-testid="equipment-detail-page">
      {/* Breadcrumb */}
      <div className="bg-muted/30 py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to={`/${equipment.division.replace('_', '-')}`}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {l.back}
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            {/* Main Image */}
            <div 
              className="aspect-[4/3] rounded-lg overflow-hidden bg-muted relative group cursor-pointer"
              onClick={() => images.length > 0 && setGalleryOpen(true)}
            >
              <img 
                src={getImageUrl(images[currentImageIndex])} 
                alt={localizedName}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button 
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                  
                  {/* Photo Count */}
                  <div className="absolute top-4 right-4 bg-black/50 text-white text-sm px-3 py-1 rounded-full">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>
            
            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {images.map((img, index) => (
                  <button
                    key={img}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentImageIndex 
                        ? 'border-primary ring-2 ring-primary/30' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={getImageUrl(img)} 
                      alt={`${localizedName} - ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Equipment Details */}
          <div>
            <div className="flex items-start justify-between gap-4 mb-2">
              <Badge variant={equipment.available ? 'default' : 'secondary'}>
                {equipment.available ? l.available : l.rented}
              </Badge>
              <span className="text-sm text-muted-foreground uppercase tracking-wide">
                {localizedCategory}
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl tracking-tight mb-2">
              {localizedName}
            </h1>
            
            {localizedSubheading && (
              <h2 className="text-xl text-muted-foreground mb-6">
                {localizedSubheading}
              </h2>
            )}

            <div className="text-muted-foreground leading-relaxed mb-8 whitespace-pre-line">
              {localizedDescription}
            </div>

            {/* Rates */}
            <div className="bg-muted/30 rounded-lg p-6 mb-8">
              <h3 className="font-medium mb-4">{l.rates}</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-card rounded-lg border border-border">
                  <p className="text-sm text-muted-foreground mb-1">{l.daily}</p>
                  <p className="text-2xl font-semibold text-primary">${equipment.daily_rate?.toFixed(2)}</p>
                </div>
                <div className="text-center p-4 bg-card rounded-lg border border-border">
                  <p className="text-sm text-muted-foreground mb-1">{l.weekly}</p>
                  <p className="text-2xl font-semibold">${equipment.weekly_rate?.toFixed(2)}</p>
                </div>
                <div className="text-center p-4 bg-card rounded-lg border border-border">
                  <p className="text-sm text-muted-foreground mb-1">{l.monthly}</p>
                  <p className="text-2xl font-semibold">${equipment.monthly_rate?.toFixed(2) || '—'}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button 
                variant="outline" 
                size="lg"
                className="flex-1 rounded-full"
                onClick={() => setCalendarOpen(true)}
              >
                <CalendarIcon className="h-5 w-5 mr-2" /> {l.viewCalendar}
              </Button>
              <Button 
                size="lg"
                className="flex-1 rounded-full"
                onClick={() => setDialogOpen(true)}
              >
                {l.requestRental}
              </Button>
            </div>
          </div>
        </div>

        {/* Suggested Accessories */}
        {suggestedItems.length > 0 && (
          <div className="mt-16 pt-12 border-t border-border">
            <h3 className="text-2xl font-light tracking-tight mb-8">{l.suggestedAccessories}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {suggestedItems.map((item) => {
                const itemImage = item.images?.length > 0 ? item.images[0] : item.image_url;
                const itemName = item.name;
                const itemCategory = language === 'fr' && item.category_fr ? item.category_fr : item.category;
                
                return (
                  <Link 
                    key={item.id} 
                    to={`/equipment/${item.id}`}
                    className="group bg-card border border-border/60 rounded-lg overflow-hidden hover:border-secondary/50 transition-all"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-muted">
                      <img 
                        src={getImageUrl(itemImage)} 
                        alt={itemName}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{itemCategory}</p>
                      <h4 className="font-medium line-clamp-1 group-hover:text-primary transition-colors">{itemName}</h4>
                      <p className="text-sm text-primary mt-1">${item.daily_rate?.toFixed(2)}/day</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Full Gallery Dialog */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-5xl p-0 bg-black">
          <div className="relative">
            <button 
              onClick={() => setGalleryOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white/20 rounded-full text-white hover:bg-white/30"
            >
              <X className="h-5 w-5" />
            </button>
            
            <div className="aspect-video relative">
              <img 
                src={getImageUrl(images[currentImageIndex])} 
                alt={`${localizedName} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-contain"
              />
              
              {images.length > 1 && (
                <>
                  <button 
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 rounded-full text-white hover:bg-white/30"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button 
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 rounded-full text-white hover:bg-white/30"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 p-4 justify-center bg-black/80">
                {images.map((img, index) => (
                  <button
                    key={img}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                      index === currentImageIndex ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={getImageUrl(img)} 
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Availability Calendar Dialog */}
      <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{l.availabilityFor} {localizedName}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <Calendar
              mode="multiple"
              selected={blockedDates}
              className="rounded-md border"
              modifiers={{
                blocked: blockedDates
              }}
              modifiersStyles={{
                blocked: { backgroundColor: 'hsl(var(--destructive))', color: 'white', borderRadius: '4px' }
              }}
              disabled={(date) => date < new Date()}
            />
          </div>
          <p className="text-sm text-muted-foreground text-center">{l.blockedDates}</p>
          <Button variant="outline" className="w-full rounded-full mt-2" onClick={() => setCalendarOpen(false)}>
            {l.close}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Rental Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{l.requestRental}: {localizedName}</DialogTitle>
          </DialogHeader>
          
          <div className="flex gap-2 mt-4">
            <Button 
              variant={requestType === 'specific' ? 'default' : 'outline'} 
              size="sm"
              className="flex-1 rounded-full"
              onClick={() => setRequestType('specific')}
            >
              {l.specificDates}
            </Button>
            <Button 
              variant={requestType === 'inquiry' ? 'default' : 'outline'} 
              size="sm"
              className="flex-1 rounded-full"
              onClick={() => setRequestType('inquiry')}
            >
              {l.generalInquiry}
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>{l.yourName} *</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                required
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{l.email} *</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{l.phone}</Label>
                <Input
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {requestType === 'specific' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{l.startDate} *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{l.endDate} *</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    min={formData.start_date || new Date().toISOString().split('T')[0]}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            <div>
              <Label>{l.message}</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                placeholder={requestType === 'inquiry' ? (language === 'fr' ? 'Questions sur cet équipement...' : 'Questions about this equipment...') : ''}
                className="mt-1"
                rows={3}
              />
            </div>

            <Button type="submit" className="w-full rounded-full" disabled={submitting}>
              {submitting ? l.submitting : l.submit}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
